// ========================================================================
// SocialOS SDK - Main API Client
// ========================================================================

import { ApiResponse, User, Session, AuthResponse, SignUpCredentials, SignInCredentials, UpdateProfileData, QueryOptions, InsertOptions, UpdateOptions } from './types';
import { RetryHandler, TimeoutHandler } from './retry';
import { ErrorHandler, ResponseValidator, NetworkError} from './errors';
import { OfflineQueue } from './offline';
import { WebSocketManager } from './websocket';
import type { StorageAdapter } from './types';
import { createStorageAdapter } from './storage';
// // import { File, Search, Upload, User } from 'lucide-react'

export class SocialOSClient {
  private baseUrl: string;
  private token: string | null = null;
  private user: User | null = null;
  private retryHandler: RetryHandler;
  private errorHandler: ErrorHandler;
  private offlineQueue: OfflineQueue;
  private websocketManager: WebSocketManager | null = null;
  private storage: StorageAdapter;
  private isOnline: boolean = true;
  private queryClient: { invalidateQueries?: (opts: Record<string, unknown>) => void } | null = null;
  private eventListeners: Map<string, Set<(data: unknown) => void>> = new Map();

  constructor(config: {
    baseUrl?: string;
    retryConfig?: Record<string, unknown>;
    storage?: StorageAdapter;
    queryClient?: { invalidateQueries?: (opts: Record<string, unknown>) => void } | null;
  } = {}) {
    this.baseUrl = config.baseUrl || this.getBaseUrl();
    this.retryHandler = new RetryHandler(config.retryConfig);
    this.errorHandler = ErrorHandler.getInstance();
    this.storage = config.storage || createStorageAdapter();
    this.offlineQueue = new OfflineQueue(this.storage);
    this.websocketManager = new WebSocketManager(this.baseUrl);
    this.queryClient = config.queryClient;
    this.initialize();
  }

  private getBaseUrl(): string {
    const envUrl = typeof window !== 'undefined'
      ? (process.env.VITE_API_URL || process.env.NEXT_PUBLIC_API_URL)
      : process.env.VITE_API_URL || process.env.NEXT_PUBLIC_API_URL;
    return envUrl || 'https://api.higherup.ai';
  }

  private async initialize(): Promise<void> {
    await this.loadSession();
    this.setupOnlineDetection();
    this.startOfflineQueueProcessing();
  }

  private setupOnlineDetection(): void {
    if (typeof window === 'undefined') return;
    this.isOnline = navigator.onLine;
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.emit('online');
    });
    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.emit('offline');
    });
  }

  private async loadSession(): Promise<void> {
    try {
      const token = await this.storage.getItem('auth_token');
      const userData = await this.storage.getItem('auth_user');
      if (token) this.token = token;
      if (userData) this.user = JSON.parse(userData);
    } catch (error) {
      this.errorHandler.handle(error);
    }
  }

  private async saveSession(session: Session): Promise<void> {
    try {
      await this.storage.setItem('auth_token', session.access_token);
      await this.storage.setItem('auth_user', JSON.stringify(session.user));
      this.token = session.access_token;
      this.user = session.user;
      this.emit('sessionChanged', session);
    } catch (error) {
      this.errorHandler.handle(error);
    }
  }

  private async clearSession(): Promise<void> {
    try {
      await this.storage.removeItem('auth_token');
      await this.storage.removeItem('auth_user');
      this.token = null;
      this.user = null;
      this.emit('sessionChanged', null);
    } catch (error) {
      this.errorHandler.handle(error);
    }
  }

  // ============ Event System ============

  on(event: string, callback: (data: unknown) => void): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback);
    return () => this.eventListeners.get(event)?.delete(callback);
  }

  private emit(event: string, data?: unknown): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.forEach((callback) => {
        try { callback(data); } catch (e) { this.errorHandler.handle(e as Error); }
      });
    }
  }

  // ============ Authentication ============

  async signUp(credentials: SignUpCredentials): Promise<AuthResponse> {
    const response = await this.makeRequest<AuthResponse>(
      `${this.baseUrl}/api/auth/signup`,
      { method: 'POST', body: JSON.stringify(credentials) }
    );
    if (response.success && response.data) {
      await this.saveSession(response.data.session);
    }
    return response.data!;
  }

  async signIn(credentials: SignInCredentials): Promise<AuthResponse> {
    const response = await this.makeRequest<AuthResponse>(
      `${this.baseUrl}/api/auth/login`,
      { method: 'POST', body: JSON.stringify(credentials) }
    );
    if (response.success && response.data) {
      await this.saveSession(response.data.session);
    }
    return response.data!;
  }

  async signOut(): Promise<void> {
    try {
      await this.makeRequest(`${this.baseUrl}/api/auth/logout`, { method: 'POST' });
    } finally {
      await this.clearSession();
      this.websocketManager?.disconnect();
    }
  }

  async getSession(): Promise<Session | null> {
    if (!this.token) return null;
    try {
      const response = await this.makeRequest<{ user: User }>(`${this.baseUrl}/api/auth/me`);
      if (response.success && response.data) {
        const session: Session = {
          access_token: this.token!,
          user: response.data.user,
          expires_at: Date.now() + 7 * 24 * 60 * 60 * 1000
        };
        return session;
      }
    } catch {
      await this.clearSession();
    }
    return null;
  }

  getCurrentUser(): User | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  async updateProfile(data: UpdateProfileData): Promise<User> {
    const response = await this.makeRequest<{ user: User }>(
      `${this.baseUrl}/api/auth/update`,
      { method: 'PATCH', body: JSON.stringify(data) }
    );
    if (response.success && response.data) {
      this.user = response.data.user;
      await this.storage.setItem('auth_user', JSON.stringify(this.user));
      this.emit('profileUpdated', this.user);
    }
    return response.data!.user;
  }

  async resetPassword(email: string): Promise<void> {
    await this.makeRequest(
      `${this.baseUrl}/api/auth/reset-password`,
      { method: 'POST', body: JSON.stringify({ email }) }
    );
  }

  // ============ Data Operations ============

  async query<T = unknown>(table: string, options: QueryOptions = {}): Promise<ApiResponse<T[]>> {
    const params = new URLSearchParams();
    if (options.select) {
      const select = Array.isArray(options.select) ? options.select.join(',') : options.select;
      params.set('select', select);
    }
    if (options.filter) {
      Object.entries(options.filter).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          Object.entries(value).forEach(([op, val]) => params.append(`${key}.${op}`, String(val)));
        } else {
          params.append(`${key}.eq`, String(value));
        }
      });
    }
    if (options.orderBy) {
      const order = typeof options.orderBy === 'string'
        ? options.orderBy
        : `${options.orderBy.column}.${options.orderBy.direction}`;
      params.set('order', order);
    }
    if (options.limit) params.set('limit', String(options.limit));
    if (options.offset) params.set('offset', String(options.offset));

    const url = `${this.baseUrl}/api/data/${table}?${params.toString()}`;
    const response = await this.makeRequest<{ data: T[] }>(url);
    return {
      success: response.success,
      data: (response.data as Record<string, unknown>)?.['data'] as T[] ?? (response.data as T[]) ?? [],
      error: response.error
    } as ApiResponse<T[]>;
  }

  async insert<T = unknown>(table: string, data: Record<string, unknown> | unknown[], _options: InsertOptions = {}): Promise<ApiResponse<T>> {
    if (!this.isOnline) return this.queueOfflineOperation('insert', table, data);
    const response = await this.makeRequest<{ data: T }>(`${this.baseUrl}/api/data/${table}`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    const raw = response.data as Record<string, unknown> | undefined;
    const resultData = (raw?.['data'] ?? raw) as T;
    if (response.success) this.emit('dataChanged', { type: 'insert', table, data: resultData });
    return {
      success: response.success,
      data: resultData,
      error: response.error
    } as ApiResponse<T>;
  }

  async update<T = unknown>(table: string, data: Record<string, unknown>, _options: UpdateOptions = {}): Promise<ApiResponse<T>> {
    if (!this.isOnline) return this.queueOfflineOperation('update', table, data);
    const response = await this.makeRequest<{ data: T }>(`${this.baseUrl}/api/data/${table}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
    const raw = response.data as Record<string, unknown> | undefined;
    const resultData = (raw?.['data'] ?? raw) as T;
    if (response.success) this.emit('dataChanged', { type: 'update', table, data: resultData });
    return {
      success: response.success,
      data: resultData,
      error: response.error
    } as ApiResponse<T>;
  }

  async delete<T = unknown>(table: string, id: string): Promise<ApiResponse<T>> {
    if (!this.isOnline) return this.queueOfflineOperation('delete', table, { id });
    const response = await this.makeRequest<{ data: T }>(`${this.baseUrl}/api/data/${table}?id=${id}`, {
      method: 'DELETE'
    });
    const raw = response.data as Record<string, unknown> | undefined;
    const resultData = (raw?.['data'] ?? raw) as T;
    if (response.success) this.emit('dataChanged', { type: 'delete', table, data: { id } });
    return {
      success: response.success,
      data: resultData,
      error: response.error
    } as ApiResponse<T>;
  }

  // ============ RPC ============

  async rpc(fn: string, params?: Record<string, unknown>): Promise<unknown> {
    return this.makeRequest(
      `${this.baseUrl}/api/rpc/${fn}`,
      { method: 'POST', body: params ? JSON.stringify(params) : undefined }
    );
  }

  // ============ Functions (for Stripe, etc.) ============

  get functions() {
    return {
      invoke: async (name: string, opts: Record<string, unknown> = {}) => {
        return this.makeRequest(
          `${this.baseUrl}/api/functions/${name}`,
          { method: 'POST', body: opts.body ? JSON.stringify(opts.body) : undefined }
        );
      }
    };
  }

  // ============ File Upload ============

  async uploadFile(file: File, options: Record<string, unknown> = {}): Promise<unknown> {
    if (!this.isOnline) {
      return { success: false, error: { code: 'OFFLINE_ERROR', message: 'Cannot upload files while offline' } };
    }
    const formData = new FormData();
    formData.append('file', file);
    if (options.path) formData.append('path', options.path);

    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && options.onProgress) options.onProgress((e.loaded / e.total) * 100);
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const data = JSON.parse(xhr.responseText);
          resolve({ success: true, data });
        } else {
          const error = JSON.parse(xhr.responseText);
          resolve({ success: false, error });
        }
      });
      xhr.addEventListener('error', () => {
        resolve({ success: false, error: { code: 'UPLOAD_ERROR', message: 'Upload failed' } });
      });
      xhr.open('POST', `${this.baseUrl}/api/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      xhr.send(formData);
    });
  }

  // ============ WebSocket ============

  connectWebSocket(): void { this.websocketManager?.connect(); }
  disconnectWebSocket(): void { this.websocketManager?.disconnect(); }
  onWebSocketEvent(event: string, callback: (data: unknown) => void): () => void {
    return this.websocketManager?.subscribe(event, callback) || (() => {});
  }
  sendWebSocketEvent(event: string, payload: unknown): void { this.websocketManager?.send(event as WebSocketEventType, payload); }
  getWebSocketState() { return this.websocketManager?.getState(); }

  // ============ Search Methods ============

  async search(query: string, options: Record<string, string> = {}): Promise<unknown> {
    const params = new URLSearchParams({ q: query, ...options });
    return this.makeRequest(`${this.baseUrl}/api/search?${params.toString()}`);
  }

  async searchUsers(query: string, options: Record<string, string> = {}): Promise<unknown> {
    const params = new URLSearchParams({ q: query, ...options });
    return this.makeRequest(`${this.baseUrl}/api/search/users?${params.toString()}`);
  }

  async searchPosts(query: string, options: Record<string, string> = {}): Promise<unknown> {
    const params = new URLSearchParams({ q: query, ...options });
    return this.makeRequest(`${this.baseUrl}/api/search/posts?${params.toString()}`);
  }

  async searchGroups(query: string, options: Record<string, string> = {}): Promise<unknown> {
    const params = new URLSearchParams({ q: query, ...options });
    return this.makeRequest(`${this.baseUrl}/api/search/groups?${params.toString()}`);
  }

  // ============ File Operations ============

  async deleteFile(key: string): Promise<unknown> {
    return this.makeRequest(`${this.baseUrl}/api/upload/${key}`, { method: 'DELETE' });
  }

  async uploadMultipleFiles(files: File[], options: Record<string, unknown> = {}): Promise<unknown> {
    if (!this.isOnline) {
      return { success: false, error: { code: 'OFFLINE_ERROR', message: 'Cannot upload while offline' } };
    }
    const formData = new FormData();
    files.forEach((f: File) => formData.append('files', f));
    if (options.path) formData.append('path', options.path);
    return this.makeRequest(`${this.baseUrl}/api/upload/multiple`, {
      method: 'POST',
      body: formData
    });
  }

  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.makeRequest<{ status: string }>(`${this.baseUrl}/api/health`);
      return response.success && response.data?.status === 'ok';
    } catch { return false; }
  }

  getOfflineQueueSize(): number { return this.offlineQueue.getSize(); }

  // ============ Private Helpers ============

  private async makeRequest<T = unknown>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    return this.retryHandler.execute(async () => {
      if (!this.isOnline) throw new NetworkError('No internet connection');
      const response = await TimeoutHandler.withTimeout(
        fetch(url, {
          ...options,
          headers: { ...this.getHeaders(), ...options.headers }
        }),
        30000
      );
      return ResponseValidator.validate<T>(response);
    });
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    return headers;
  }

  private async queueOfflineOperation(
    type: 'insert' | 'update' | 'delete',
    table: string,
    data: Record<string, unknown> | Record<string, unknown>[]
  ): Promise<ApiResponse<unknown>> {
    return new Promise((resolve) => {
      this.offlineQueue.add({ type, table, data, maxRetries: 3 });
      resolve({ success: true, data } as ApiResponse<unknown>);
    });
  }

  private startOfflineQueueProcessing(): void {
    setInterval(() => {
      if (this.isOnline && this.token) {
        this.offlineQueue.process(async (op) => {
          const url = `${this.baseUrl}/api/data/${op.table}`;
          const response = await this.makeRequest(url, {
            method: op.type.toUpperCase(),
            body: JSON.stringify(op.data)
          });
          return response.data;
        });
      }
    }, 30000);
  }
}

// ============ Singleton ============

let clientInstance: SocialOSClient | null = null;

export function createClient(config?: ConstructorParameters<typeof SocialOSClient>[0]): SocialOSClient {
  if (!clientInstance) clientInstance = new SocialOSClient(config);
  return clientInstance;
}

export function getClient(): SocialOSClient {
  if (!clientInstance) clientInstance = createClient();
  return clientInstance;
}
